import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader, useAuthUser } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  sanitizeDoctorUpdatePayload,
  toastError,
  toastSuccess,
} from "../../utils/utils";
import { useGetOrganizations } from "../HandlingComponents/";

// GET /doctors
export const useGetDoctors = ({ enabled = true, active } = {}) => {
  const authHeader = useAuthHeader();
  const authUser = useAuthUser();
  const organizationsQuery = useGetOrganizations();

  return useQuery({
    queryKey: ["doctors", { active: active ?? "all" }],
    queryFn: async () => {
      const params = {};
      if (active !== undefined) {
        params.active = active;
      }

      const res = await axiosInstance.get(`/doctors`, {
        headers: { Authorization: authHeader() },
        params,
      });

      if (authUser()?.role !== 1) return res.data;

      let data = res.data;
      let doctors = [];

      for (let d of data.doctors) {
        let org = organizationsQuery.data.organizations.find(
          (org) => org.id === d.org_id
        );

        // add org_name to doctor object
        doctors.push({
          ...d,
          org_name: org.name,
        });
      }

      data["doctors"] = doctors;
      return data;
    },
    enabled: enabled && organizationsQuery.isSuccess,
  });
};

// GET /doctors/:doctor_id
export const useGetDoctorById = (doctor_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["doctors", doctor_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/doctors/${doctor_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: doctor_id !== undefined && doctor_id !== null,
  });
};

// === PUT /doctors/:doctor_id ===
export const usePutDoctorById = (doctor_id, closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const getOriginalData = useGetDoctorById(doctor_id);

  return useMutation({
    mutationFn: async (editedDoctor) => {
      if (editedDoctor === undefined) return;
      const payload = sanitizeDoctorUpdatePayload(editedDoctor);
      const res = await axiosInstance.put(
        `/doctors/${doctor_id}`,
        payload,
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["doctors", doctor_id]); // update views inside ViewDoctor
      queryClient.invalidateQueries(["doctors"]); // update views inside Doctors page
      toast.success("Dettagli professionista aggiornati!", toastSuccess);
    },
    onError: (data, variables, context) => {
      queryClient.setQueryData(["doctors", doctor_id], (oldData) => {
        return {
          ...oldData,
          ...getOriginalData.data,
        };
      });
      toast.error("Dettagli professionista non aggiornati", toastError);
    },
  });
};

// === POST /doctors ===
export const useNewDoctor = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newDoctor) => {
      const resDoctor = await axiosInstance.post(`/doctors`, newDoctor, {
        headers: {
          Authorization: authHeader(),
        },
      });
      return resDoctor.data;
    },
    onSuccess: (data) => {
      // close modal and display toast
      toast.success("Professionista aggiunto con successo!", toastSuccess);
      closeModal();

      queryClient.invalidateQueries(["doctors"]);
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error ||
        "Errore nell'aggiunta del professionista";
      toast.error(errorMessage, toastError);
    },
  });
};

// === DELETE /doctors ===
export const useDeleteDoctor = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (doctor_id) => {
      const res = await axiosInstance.delete(`/doctors/${doctor_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    onSuccess: () => {
      // close modal and display toast
      toast.success("Professionista rimosso con successo!", toastSuccess);
      queryClient.invalidateQueries(["doctors"]);
    },
    onError: () => {
      // close modal and display error
      toast.error("Errore nella rimozione del professionista", toastError);
    },
  });
};
